// EventPicker — job-costing: tag a receipt to a show/event. Server-backed:
// empty query → the buyer's OWN crew events nearest the receipt date (low-friction
// default); typing → searches ALL events (they may file for a show they aren't
// crewed on). Mirrors the UserPicker interaction; selection is a clearable chip.

import React, { useEffect, useState } from 'react';
import useReceiptsStore from '../stores/useReceiptsStore';
import { EventOption } from './types';
import { formatEventDate } from '../utils/date-utils';
import { buttonClass } from '../components/ui/Button';
import { Combobox, ComboboxOption } from '../components/ui/Combobox';
import { Tag } from '../components/ui/Tag';
import FadeIn from '../components/ui/FadeIn';

export default function EventPicker({
  value, valueName, date, onSelect, onClear, disabled = false,
}: {
  value: string;
  valueName: string | null;
  date: string;
  onSelect: (e: EventOption) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const { fetchEventOptions } = useReceiptsStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [opts, setOpts] = useState<EventOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Load whenever the dropdown is open: default (date-scoped, mine) or search (all).
  // Debounced ~250ms so typing doesn't fire a request per keystroke; `alive` still
  // guards against a slow response landing after a newer query superseded it.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      fetchEventOptions({ date: query.trim() ? undefined : date, q: query.trim() || undefined })
        .then((list) => { if (alive) setOpts(list || []); })
        .catch(() => { if (alive) setOpts([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [open, query, date, fetchEventOptions]);

  // Selected + not actively editing → show the chip.
  if (value && valueName && !open) {
    return (
      <FadeIn key="chip" className="flex items-center gap-2">
        <Tag tone="accent" className="max-w-full truncate">{valueName}</Tag>
        {!disabled && (
          <>
            <button onClick={() => { setQuery(''); setOpen(true); }} className={`${buttonClass('quiet', 'sm')} shrink-0`}>change</button>
            <button onClick={onClear} className={`${buttonClass('quiet', 'sm')} shrink-0`} aria-label="Clear event">clear</button>
          </>
        )}
      </FadeIn>
    );
  }

  const options: ComboboxOption[] = opts.map((e) => ({
    id: e.id,
    label: e.name,
    sublabel: `${e.event_date ? formatEventDate(e.event_date) : ''}${e.location ? ` · ${e.location}` : ''}`.trim() || null,
  }));

  const none = loading ? undefined : query.trim() ? 'No matching shows' : 'No nearby shows, type to search all';

  return (
    <FadeIn key="pick">
    <Combobox
      label="Show"
      options={options}
      value={null}
      disabled={disabled}
      autoFocus={!!value}
      filterOptions={false}
      busy={loading}
      onQueryChange={setQuery}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onSelect={(o) => {
        const e = o && opts.find((x) => String(x.id) === String(o.id));
        if (e) { onSelect(e); setOpen(false); setQuery(''); }
      }}
      placeholder="Search shows, or pick from yours…"
      emptyMessage={none}
      noOptionsMessage={none}
      chevron={false}
    />
    </FadeIn>
  );
}
